import { useNavigate } from "react-router-dom";
import "../../styles/studio/visualization/studioVisualizationCard.css";
import { truncateText, formatCount } from "../../utils/text.js";
import {
  publishVisualization,
  unpublishVisualization,
  deleteVisualization,
} from "../../services/visualizations.service.js";
import { useState } from "react";
import { formatDate } from "../../utils/date.js";
import { AiOutlineEye, AiOutlineHeart, AiOutlineMessage } from "react-icons/ai";

const MAX_TITLE = 60;
const MAX_DESC = 120;
const MAX_TAGS_VISIBLE = 3;

const StudioVisualizationCard = ({ data, onDelete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [publication, setPublication] = useState(data);

  const visibleTags = publication.tags.slice(0, MAX_TAGS_VISIBLE);
  const extraTags = publication.tags.length - visibleTags.length;

  const handleEdit = () => {
    navigate(`/studio/visualizations/${publication.publicationId}`);
  };

  const handleView = () => {
    navigate(`/v/visualizations/${publication.publicationId}`);
  };

  const handleDelete = async () => {
    if (!confirm(`Delete "${publication.title}"? This cannot be undone.`)) return;
    setLoading(true);
    try {
      await deleteVisualization(publication.publicationId);
      onDelete?.(publication.publicationId);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const togglePublish = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const updatedPublication = publication.isPublished
        ? await unpublishVisualization(publication.publicationId)
        : await publishVisualization(publication.publicationId);
      setPublication(updatedPublication);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="studio-card">
      <div className="studio-card-header">
        <span
          className={`status ${
            publication.isPublished ? "published" : "draft"
          }`}
        >
          {publication.isPublished ? "Published" : "Draft"}
        </span>

        <span className="date">
          {publication.isPublished
            ? `Published · ${formatDate(publication.publishedAt)}`
            : `Created · ${formatDate(publication.createdAt)}`}
        </span>
      </div>

      <div className="studio-card-body">
        <h3 className="studio-title">
          {truncateText(publication.title, MAX_TITLE)}
        </h3>

        <p className="studio-description">
          {truncateText(publication.description, MAX_DESC)}
        </p>

        {publication.tags.length > 0 && (
          <div className="studio-tags">
            {visibleTags.map((tag, idx) => (
              <span key={idx} className="studio-tag">
                #{tag}
              </span>
            ))}
            {extraTags > 0 && (
              <span className="studio-tag more">+{extraTags}</span>
            )}
          </div>
        )}
      </div>

      <div className="studio-card-footer">
        <div className="stats">
          <div className="stats-item">
            <span>{formatCount(publication.likesCount)}</span>
            <AiOutlineHeart size={16} color="#6b7280" />
          </div>
          <div className="stats-item">
            <span>{formatCount(publication.views)}</span>
            <AiOutlineEye size={16} color="#6b7280" />
          </div>
          <div className="stats-item">
            <span>{formatCount(publication.commentsCount)}</span>
            <AiOutlineMessage size={16} color="#6b7280" />
          </div>
        </div>

        <div className="actions">
          <button onClick={publication.isPublished ? handleView : handleEdit}>
            {publication.isPublished ? "View" : "Edit"}
          </button>
          <button onClick={togglePublish} disabled={loading}>
            {publication.isPublished ? "Unpublish" : "Publish"}
          </button>
          <button onClick={handleDelete} disabled={loading} className="danger-btn">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudioVisualizationCard;
